import { useEffect, useState } from "react";
import { useEditorAuth } from "@/context/EditorAuthContext";
import { Bot, Clock, Play, RefreshCw, CheckCircle2, XCircle, SkipForward, Loader2, Pause } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const STATUS_META = {
  success:         { icon: CheckCircle2, cls: "text-[#688A4E]",   label: "Published" },
  failed:          { icon: XCircle,      cls: "text-red-500",     label: "Failed" },
  in_progress:     { icon: Loader2,      cls: "text-amber-500 animate-spin", label: "In progress" },
  skipped_missed:  { icon: SkipForward,  cls: "text-gray-400",    label: "Skipped (missed)" },
  skipped_paused:  { icon: Pause,        cls: "text-gray-400",    label: "Skipped (paused)" },
};

export default function AutoWriterPanel() {
  const { authAxios } = useEditorAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [toggling, setToggling] = useState(false);

  const load = () => {
    setLoading(true);
    authAxios().get("/editor/ai/auto-status")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load auto-writer status"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    if (!data) return;
    const next = !data.enabled;
    setToggling(true);
    try {
      await authAxios().post("/editor/ai/auto-config", { enabled: next });
      setData({ ...data, enabled: next });
      toast.success(next ? "Auto-Writer resumed" : "Auto-Writer paused");
    } catch {
      toast.error("Could not update setting");
    } finally {
      setToggling(false);
    }
  };

  const fireNow = async () => {
    if (!window.confirm("Generate and publish ONE article now using GPT-5.2? This may take 30-90 seconds.")) return;
    setRunning(true);
    try {
      const r = await authAxios().post("/editor/ai/auto-run");
      toast.success(`Published: ${r.data.topic}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-run failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div className="text-gray-500" data-testid="autowriter-loading">Loading…</div>;
  if (!data) return null;

  return (
    <div className="space-y-8" data-testid="autowriter-panel">
      {/* Config card */}
      <div className="bg-white border border-gray-200 p-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-2 h-2 rounded-full ${data.enabled ? "bg-[#688A4E] animate-pulse" : "bg-gray-300"}`}></div>
              <div className="overline text-[#688A4E]">GPT-5.2 · Auto-Writer</div>
            </div>
            <div className="font-serif text-3xl text-[#0A1128] mb-2">
              {data.enabled ? "Daily automation is running." : "Daily automation is paused."}
            </div>
            <p className="text-sm text-gray-600 max-w-xl">
              Publishes {data.daily_slots.length} SEO-optimised articles per day, every day, on Singapore-time slots.
              Topics rotate through your keyword queue and a curated fallback pool.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={toggleEnabled}
              disabled={toggling}
              data-testid="autowriter-toggle"
              className={`px-5 py-2.5 text-sm border transition flex items-center gap-2 ${
                data.enabled
                  ? "border-gray-300 text-[#0A1128] hover:bg-gray-50"
                  : "border-[#688A4E] bg-[#688A4E] text-white hover:bg-[#5a7a41]"
              }`}
            >
              {data.enabled ? <><Pause className="w-4 h-4"/> Pause automation</> : <><Play className="w-4 h-4"/> Resume automation</>}
            </button>
            <button
              onClick={fireNow}
              disabled={running}
              data-testid="autowriter-run-now"
              className="btn-primary"
            >
              {running ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating…</> : <><Bot className="w-4 h-4"/> Run one now</>}
            </button>
            <button
              onClick={load}
              data-testid="autowriter-refresh"
              className="px-4 py-2.5 text-sm border border-gray-300 text-gray-600 hover:text-[#0A1128] flex items-center gap-2"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4"/>
            </button>
          </div>
        </div>

        {/* Slots */}
        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          {data.daily_slots.map((s) => (
            <div key={s} className="border border-gray-200 px-5 py-4 flex items-center gap-3" data-testid={`autowriter-slot-${s}`}>
              <Clock className="w-5 h-5 text-[#688A4E]"/>
              <div>
                <div className="font-serif text-xl text-[#0A1128]">{s} SGT</div>
                <div className="text-xs text-gray-500">Singapore time · daily</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent runs */}
      <div className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="overline">Recent runs</div>
          <div className="text-xs text-gray-500">{data.recent_runs.length} entries</div>
        </div>
        {data.recent_runs.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500" data-testid="autowriter-empty">
            No runs yet. The daily scheduler will fire at {data.daily_slots.join(", ")} SGT.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F7] text-left text-xs tracking-widest uppercase text-gray-500">
                <tr>
                  <th className="px-6 py-3">Slot</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Topic</th>
                  <th className="px-6 py-3">Run at (UTC)</th>
                  <th className="px-6 py-3 text-right">Article</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_runs.map((r, idx) => {
                  const meta = STATUS_META[r.status] || { icon: Bot, cls: "text-gray-400", label: r.status };
                  const Icon = meta.icon;
                  return (
                    <tr key={r.slot_id + idx} className="border-t border-gray-100 hover:bg-[#F9F9F7]" data-testid={`autowriter-run-${r.slot_id}`}>
                      <td className="px-6 py-4 font-mono text-xs text-gray-700">{r.slot_id}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-xs ${meta.cls}`}>
                          <Icon className="w-4 h-4"/> {meta.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-700 max-w-md truncate">{r.topic || <span className="text-gray-400">—</span>}</td>
                      <td className="px-6 py-4 text-xs text-gray-500">{r.run_at ? new Date(r.run_at).toLocaleString() : "—"}</td>
                      <td className="px-6 py-4 text-right">
                        {r.article_id ? (
                          <Link to={`/editor/articles/${r.article_id}`} className="text-[#688A4E] hover:underline text-xs">Open →</Link>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
